import { getUser } from '../../helpers/axiosHelper';
import { disablePushNotifications } from '../../helpers/pushNotifications';
import { getCartSlice, getUserSlice } from './store';

let latestRequest = 0;
// Bumped only on an actual identity transition (logout or a new session
// starting), never on routine /user re-sync. A per-component ref cannot
// stand in for this: a private route (e.g. Purchase) is unmounted by
// ProtectedRoute on logout, so a ref living in that instance never gets the
// chance to record the transition. This counter lives outside any component,
// so it still advances even when nothing is mounted to react to it — closing
// the gap where a logout followed by a same-second relogin (JWTs here carry
// only second-resolution `iat`, so the two tokens can be byte-identical)
// would otherwise leave a stale in-flight request looking like it still
// belongs to the current session.
let sessionGeneration = 0;
export const getSessionGeneration = () => sessionGeneration;

const authorizationFor = (headers = {}) => headers.Authorization || '';
const hasUsableProductCart = (cart) => cart.every((item) => (
    item
    && typeof item === 'object'
    && item.itemType !== 'combo'
    && (
        (typeof item._id === 'string' && !!item._id.trim())
        || (typeof item.id === 'string' && !!item.id.trim())
    )
    && typeof item.quantityToBuy === 'number'
    && Number.isFinite(item.quantityToBuy)
    && item.quantityToBuy > 0
));
const hasUsableUserPayload = (user) => (
    user
    && typeof user === 'object'
    && typeof user.name === 'string'
    && !!user.name.trim()
    && Array.isArray(user.cart)
    && hasUsableProductCart(user.cart)
    && Number.isInteger(user.cartRevision)
    && user.cartRevision >= 0
    && typeof user.wallet === 'number'
    && Number.isFinite(user.wallet)
);

const removeSessionStorage = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
};

export const clearAuthenticatedUser = () => {
    const headers = getUserSlice.getState().headers;
    void disablePushNotifications({ headers, silent: true });
    latestRequest += 1;
    sessionGeneration += 1;
    removeSessionStorage();
    getUserSlice.getState().clearPrivateUser();
    getCartSlice.getState().clearProductItems();
    // The CAS token belongs to the session that is ending. Carrying it over
    // would make the next session's first write compare against a foreign
    // revision and fail with a spurious conflict.
    getCartSlice.getState().setCartRevision(0);
};

export const startAuthenticatedUserSession = (token, name, role) => {
    latestRequest += 1;
    sessionGeneration += 1;
    // Products belong to the authenticated server user. Clearing them before
    // installing another token prevents user A's cart from being rendered
    // while user B is still hydrating. Local visual-only Combos survive.
    getCartSlice.getState().clearProductItems();
    // Same reasoning as clearAuthenticatedUser: this is a new identity, so
    // any previously held cartRevision is not this user's token.
    getCartSlice.getState().setCartRevision(0);
    if (role === undefined) {
        getUserSlice.getState().startAuthenticatedSession(token, name);
    } else {
        getUserSlice.getState().startAuthenticatedSession(token, name, role);
    }
};

export const restoreAuthenticatedUserSession = () => {
    const token = localStorage.getItem('token');
    const name = localStorage.getItem('userName');
    if (token) {
        startAuthenticatedUserSession(token, name);
        return { authenticated: true };
    }
    clearAuthenticatedUser();
    return { authenticated: false };
};

export const syncCurrentUser = async () => {
    const requestId = ++latestRequest;
    const userAtStart = getUserSlice.getState();
    const headers = userAtStart.headers;
    const authorization = authorizationFor(headers);

    if (!authorization) {
        userAtStart.finishUserSync();
        return { skipped: true };
    }

    const cartRevisionAtStart = getCartSlice.getState().productRevision;
    const walletRevisionAtStart = userAtStart.walletRevision;
    userAtStart.beginUserSync();

    try {
        const response = await getUser({ headers });
        const serverUser = response?.data;
        const userNow = getUserSlice.getState();
        if (requestId !== latestRequest || authorizationFor(userNow.headers) !== authorization) {
            return { stale: true };
        }

        if (!hasUsableUserPayload(serverUser)) {
            clearAuthenticatedUser();
            return { invalid: true };
        }

        // A local Product mutation made while /user was in flight is newer
        // than this response. Keep it until the next canonical reconciliation —
        // this guards cartRevision too: a write completed during this request
        // already installed a fresher server revision than this stale payload.
        const cartNow = getCartSlice.getState();
        if (cartNow.productRevision === cartRevisionAtStart) {
            cartNow.setCartRevision(serverUser.cartRevision);
            cartNow.replaceProductsFromServer(serverUser.cart);
        }
        userNow.applyServerUser({
            name: serverUser.name,
            role: serverUser.role,
            // A purchase confirmed while /user was pending owns the newer
            // wallet. The old response may finish synchronization, but it
            // cannot roll that balance back.
            ...(userNow.walletRevision === walletRevisionAtStart
                ? { wallet: serverUser.wallet }
                : {})
        });
        return { user: serverUser };
    } catch (error) {
        const currentUser = getUserSlice.getState();
        if (requestId !== latestRequest || authorizationFor(currentUser.headers) !== authorization) {
            return { stale: true };
        }
        if (error?.response?.status === 401) {
            clearAuthenticatedUser();
            return { expired: true };
        }
        currentUser.failUserSync('No pudimos sincronizar tu carrito. Intenta nuevamente.');
        return { error };
    }
};
